import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { pdfjs } from "react-pdf";
import {
  FaCalendarAlt,
  FaDownload,
  FaCheckCircle,
  FaCalendarPlus,
} from "react-icons/fa";

import axios from "../../api/axios";
import BottomNav from "../../components/BottomNav";
import HeaderWithBack from "../../components/HeaderWithBack";
import SendEmail from "../../components/modal/SendEmail";
import Loading from "../../components/Loading";
import LogCode from "../../components/LogCode";
import { daysFromToday, formatDateId } from "../../utils/date";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "../../styles/styles.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const getDownloadFileName = (
  contentDisposition: string | undefined,
  fallback: string,
): string => {
  if (!contentDisposition) return fallback;

  const encoded = contentDisposition.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ""));
    } catch {
      //
    }
  }

  const plain = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
  return plain?.[1]?.trim() || fallback;
};

const ReferenceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { referenceId } = useParams<{ referenceId: string }>();
  const storedData = sessionStorage.getItem("userData");
  const userData = storedData ? JSON.parse(storedData) : null;
  const token = userData ? userData.token : null;
  const email = userData ? userData.userData : null;

  const [referenceData, setReferenceData] = useState<any>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`reference/show/${referenceId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReferenceData(response.data.data);
      } catch (error) {
        console.error("Error fetching reference data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token, referenceId]);

  // Rujukan masih berlaku sampai akhir hari pada tanggal batas berlaku.
  const expiredDateText = formatDateId(referenceData?.reference_expired_date);
  const daysLeft = daysFromToday(referenceData?.reference_expired_date);
  const isExpired = daysLeft !== null && daysLeft < 0;

  const primeLogs = referenceData?.teleconsult?.prime_logs || [];
  const referenceLog =
    primeLogs.find((log: any) => log.type === "reference") ||
    primeLogs[0] ||
    null;
  // Vale: rujukan langsung dikirim ke PrimeCenter saat dokter submit summary, tanpa booking pasien
  const requiresBooking = referenceData?.requires_booking !== false;
  const isSubmitFailed = !requiresBooking && referenceLog?.status === "failed";
  const hasReferenceLog = requiresBooking
    ? !!referenceLog
    : referenceLog?.status === "success";
  const isApproved = referenceLog?.approval_status === "approved";
  const isRejected = referenceLog?.approval_status === "rejected";

  // Vale: rumah sakit rujukan mengikuti data dari PrimeCenter
  const submittedDetails = !requiresBooking ? [
    { label: "Rumah Sakit", value: referenceLog?.hospital_name },
  ].filter((item) => !!item.value) : [
    {
      label: "Rumah Sakit",
      value: referenceLog?.hospital_name || referenceData?.provider_name,
    },
    { label: "Poli", value: referenceData?.poly_name },
    {
      label: "Dokter",
      value: referenceLog?.dppj !== "-" ? referenceLog?.dppj : null,
    },
  ].filter((item) => !!item.value);

  const canBook =
    requiresBooking &&
    !hasReferenceLog &&
    !isExpired &&
    referenceData?.status?.toLowerCase() === "final";

  const handleDownloadPdf = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `reference/download/${referenceId}`,
        { hospital_id: referenceData?.hospital_id },
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" },
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = getDownloadFileName(
        response.headers?.["content-disposition"],
        `Surat-Rujukan_${referenceLog?.log_code || referenceId}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error: any) {
      let message = "Gagal mengunduh PDF";
      const errBlob = error?.response?.data;
      if (errBlob instanceof Blob) {
        try {
          const parsed = JSON.parse(await errBlob.text());
          message = parsed?.message || message;
        } catch {
          //
        }
      } else if (error?.response?.data?.message) {
        message = error.response.data.message;
      }
      alert(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await axios.get(`brilife/reference/send-email/${referenceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert("Berhasil mengirim rujukan ke email");
    } catch {
      alert("Gagal mengirim rujukan");
    } finally {
      setIsLoading(false);
      setShowModal(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: any = {
      active: { color: "bg-green-500", text: "Aktif" },
      approved: { color: "bg-green-500", text: "Disetujui" },
      final: { color: "bg-blue-500", text: "Berlaku" },
      pending: { color: "bg-yellow-500", text: "Menunggu" },
      expired: { color: "bg-red-500", text: "Kedaluwarsa" },
      rejected: { color: "bg-red-500", text: "Ditolak" },
      draft: { color: "bg-gray-400", text: "Draft" },
    };
    const config = statusConfig[status?.toLowerCase()] || {
      color: "bg-gray-500",
      text: status,
    };
    return (
      <span
        className={`${config.color} text-white px-3 py-1 rounded-full text-xs font-semibold`}
      >
        {config.text}
      </span>
    );
  };

  const formatBookedAt = (dateStr: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  if (isLoading && !referenceData) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <HeaderWithBack title="Detail Rujukan" />
        <div className="flex-1 flex items-center justify-center">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pb-20">
      {/* Header */}
      <HeaderWithBack title="Detail Rujukan" />

      {/* Status bar */}
      <div className="bg-teal-500 text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center">
          <FaCalendarAlt className="mr-2" />
          <span className="text-sm font-medium">Rujukan Anda</span>
        </div>
        {referenceData && getStatusBadge(isExpired ? "expired" : referenceData.status)}
      </div>

      {/* Masa berlaku rujukan */}
      {daysLeft !== null &&
        (isExpired ? (
          <div className="bg-red-50 border-b border-red-200 text-red-700 px-4 py-2 text-sm text-center">
            Masa berlaku rujukan habis pada <strong>{expiredDateText}</strong>
          </div>
        ) : (
          <div className="bg-yellow-100 text-yellow-800 px-4 py-2 text-sm text-center">
            Berlaku sampai <strong>{expiredDateText}</strong>
          </div>
        ))}

      <div className="px-4 mt-4 space-y-4">
        {/* ── Status Rujukan ke PrimeCenter ─────────────────────────────────── */}
        {hasReferenceLog ? (
          <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
            <div
              className={`border-b px-4 py-3 flex items-center justify-between ${
                isApproved
                  ? "bg-green-50 border-green-100"
                  : isRejected
                    ? "bg-red-50 border-red-100"
                    : "bg-yellow-50 border-yellow-100"
              }`}
            >
              <div className="flex items-center gap-2">
                <FaCheckCircle
                  className={
                    isApproved
                      ? "text-green-500"
                      : isRejected
                        ? "text-red-500"
                        : "text-yellow-500"
                  }
                  size={15}
                />
                <h2 className="font-semibold text-gray-800 text-sm">
                  Status Rujukan
                </h2>
              </div>
              <span
                className={`text-xs text-white px-2 py-0.5 rounded-full font-medium ${
                  isApproved
                    ? "bg-green-500"
                    : isRejected
                      ? "bg-red-500"
                      : "bg-yellow-500"
                }`}
              >
                {isApproved
                  ? "Disetujui PrimeCenter"
                  : isRejected
                    ? "Ditolak PrimeCenter"
                    : "Menunggu Approval PrimeCenter"}
              </span>
            </div>

            <div className="p-4 space-y-3">
              {/* Kode referensi PrimeLog, bukan kode booking rumah sakit */}
              {referenceLog?.log_code && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">Kode Referensi</p>
                  <p className="text-base font-bold text-teal-600 leading-snug">
                    <LogCode code={referenceLog.log_code} />
                  </p>
                </div>
              )}

              {/* Poli, dokter & RS yang dipilih pasien untuk kode referensi ini */}
              {submittedDetails.length > 0 && (
                <div className="space-y-2">
                  {submittedDetails.map((item) => (
                    <div
                      key={item.label}
                      className="flex justify-between gap-3 text-sm"
                    >
                      <span className="text-gray-400 shrink-0">{item.label}</span>
                      <span className="font-medium text-gray-700 text-right">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {!isApproved && !isRejected && (
                <p className="text-xs text-gray-500 text-center">
                  Rujukan ini baru bisa diunduh dan dipakai ke rumah sakit
                  setelah disetujui PrimeCenter.
                </p>
              )}

              {isRejected && referenceLog?.rejection_reason && (
                <p className="text-xs text-red-600 text-center">
                  Alasan ditolak: {referenceLog.rejection_reason}
                </p>
              )}

              {/* Unduh rujukan, hanya aktif setelah disetujui */}
              {isApproved && (
                <button
                  onClick={handleDownloadPdf}
                  disabled={isLoading}
                  className="w-full py-2.5 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  <FaDownload size={13} />
                  Unduh Rujukan
                </button>
              )}

              {referenceLog?.approved_at && (
                <div className="text-xs text-gray-400 text-right">
                  Disetujui pada {formatBookedAt(referenceLog.approved_at)}
                </div>
              )}
            </div>
          </div>
        ) : !requiresBooking ? (
          /* Vale: rujukan sedang dikirim / gagal dikirim ke PrimeCenter */
          <div
            className={`bg-white border rounded-xl p-5 flex flex-col items-center text-center gap-2 ${
              isSubmitFailed ? "border-red-200" : "border-yellow-200"
            }`}
          >
            <p className="font-semibold text-gray-700 text-sm">
              {isSubmitFailed
                ? "Rujukan Gagal Dikirim ke PrimeCenter"
                : "Rujukan Sedang Diproses"}
            </p>
            <p className="text-xs text-gray-400">
              {isSubmitFailed
                ? "Silakan hubungi customer service untuk bantuan pengajuan rujukan."
                : "Rujukan dari dokter sedang dikirim ke PrimeCenter. Silakan cek kembali beberapa saat lagi."}
            </p>
          </div>
        ) : (
          /* Belum pernah diajukan ke PrimeCenter */
          canBook && (
            <div className="bg-white border border-dashed border-teal-300 rounded-xl p-5 flex flex-col items-center text-center gap-3">
              <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center">
                <FaCalendarPlus className="text-teal-400" size={20} />
              </div>
              <div>
                <p className="font-semibold text-gray-700 text-sm">
                  Rujukan Belum Diajukan
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Pilih dokter dan jadwal untuk mengajukan rujukan ini ke
                  PrimeCenter
                </p>
              </div>
              <button
                onClick={() =>
                  navigate(`/reference/${referenceId}/booking/doctors`, {
                    state: {
                      providerCode: referenceData?.provider_code,
                      specialityCode: referenceData?.speciality_code,
                      specialityName: referenceData?.speciality_name,
                      polyCode: referenceData?.poly_code,
                      polyName: referenceData?.poly_name || referenceData?.poly,
                      sickLeaveDate: referenceData?.sick_leave_date,
                    },
                  })
                }
                className="bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors flex items-center gap-2"
              >
                <FaCalendarPlus size={13} />
                Ajukan Rujukan
              </button>
            </div>
          )
        )}

        {/* ── Informasi Rujukan ─────────────────────────────────────────────── */}
        <div className="bg-white border rounded-xl shadow-sm p-4">
          <h2 className="font-semibold text-gray-800 mb-3">
            Informasi Rujukan
          </h2>

          <div className="mb-3 pb-3 border-b">
            <p className="text-xs text-gray-400 mb-1">Nomor Rujukan</p>
            <p className="font-bold text-base">
              {referenceData?.reference_number}
            </p>
          </div>

          {requiresBooking && (
            <div className="mb-3 pb-3 border-b">
              <p className="text-xs text-gray-400 mb-1">Rumah Sakit Tujuan</p>
              <p className="font-semibold">{referenceData?.provider_name}</p>
              {referenceData?.provider_code && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Kode: {referenceData.provider_code}
                </p>
              )}
            </div>
          )}

          <div className="mb-3 pb-3 border-b">
            <p className="text-xs text-gray-400 mb-1">{referenceData?.speciality_name ? 'Spesialisasi' : 'Poliklinik'}</p>
            <p className="font-semibold">
              {referenceData?.speciality_name || referenceData?.poly_name || referenceData?.poly}
            </p>
            {(referenceData?.speciality_code || referenceData?.poly_code) && (
              <p className="text-xs text-gray-500 mt-0.5">
                Kode: {referenceData.speciality_code || referenceData.poly_code}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-400 mb-1">Tipe Rujukan</p>
              <p className="font-semibold capitalize">{referenceData?.type}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-1">Tanggal Rujukan</p>
              <p className="font-semibold">{formatDateId(referenceData?.reference_date)}</p>
            </div>
          </div>

          {referenceData?.doctor_note && (
            <div className="mt-3 bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-400 mb-1">Catatan Dokter</p>
              <p className="text-sm text-gray-700">
                {referenceData.doctor_note}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <Loading />
        </div>
      )}

      {/* Send Email Modal */}
      <SendEmail
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirm}
        email={email}
      />

      <BottomNav />
    </div>
  );
};

export default ReferenceDetail;
